import React, { useMemo, Fragment, useEffect, useState, useCallback } from 'react';
import { useSelector, useDispatch} from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import {
  Button,
  CircularProgress,
  Grid
} from '@material-ui/core';
import { BackdropCircular } from 'components';
import { updateDraftQuoteByIdApi } from 'api';
import { setSelectedDraftedQuote } from 'redux/actions/quote';
import { useFormik } from 'formik';
import { transformItemsToProducts, calculateSavings } from 'pages/quotes/helpers';
import { handleSelectDataSource } from 'pages/quotes/QuoteItems/QuoteListings/utils/handleSelectDataSource';
import { calculateTotals } from 'pages/quotes/QuoteItems/QuoteListings/utils/calculateTotals';
import { useStyles } from 'pages/quotes/styles';
import EditQuoteListingsGroupSelect from './EditQuoteListingsGroupSelect';
import EditQuoteListingsItemRow from './EditQuoteListingsItemRow';
import EditQuoteListingsFooter from './EditQuoteListingsFooter';
import { createValidationSchema } from './validation';


const EditQuoteListings = () => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const history = useHistory();
  const { id, quoteId } = useParams<{ id: string, quoteId: string }>();
  const [isLoading, setIsLoading] = useState(true);
  const [calculatedTotals, setCalculatedTotals] = useState({});

  const {
    loadingDraftedQuotes,
    draftedQuotes,
    loadingQuoteCustomers,
    quoteProductsByGroup,
    quoteProductsLoading,
    quoteProducts,
    customerPushCart,
    loadingQuoteProductSubscriptionOptions,
    subscriptionOptionsBySku,
    patientsGroupCart,
    quoteGroupsAttributeData
  } = useSelector((state: IReduxState) => state.quote);

  const loading =
    loadingDraftedQuotes ||
    loadingQuoteCustomers ||
    loadingQuoteProductSubscriptionOptions ||
    quoteProductsLoading;

  const selectedQuote = draftedQuotes.find((quote) => quote._id === quoteId);

  useEffect(() => {
    if (selectedQuote?.selectedItems?.length !== 0) {
      const transformedProductsData = transformItemsToProducts(patientsGroupCart, quoteProducts, subscriptionOptionsBySku);
      const initialValues = {
        items: (selectedQuote?.selectedItems || []).map((item) => {
          const {
            sku,
            qty,
            price: updatedPrice,
            custom_subscription_price,
            product_option: {
              extension_attributes: {
                quotepro_discounts,
                subscription_id,
              },
            },
          } = item;

          const {
            product: {
              catalog_quotepro_groups,
              price,
              subscription_options,
              subscription_active,
              additional_subscription_price,
              savings = 0
            },
          } = selectedQuote?.products[sku] || transformedProductsData[sku] || {};

          const groupForAllItems = quoteGroupsAttributeData?.options?.find((group) => group.label === 'All');
          const selectedItemGroup = catalog_quotepro_groups?.toString().split(',')[0] || groupForAllItems?.value || '';
          const fixedPrice = updatedPrice ? updatedPrice : price || 0;
          const discountType = quotepro_discounts?.price_type || '';

          const isSubscriptionActive = subscription_active == 1;
          const subscriptionFrequency = subscription_options?.find((option) => option.id === subscription_id)?.frequency_count || '';
          const discount = quotepro_discounts?.price || '';


          return {
            selectedItemGroup,
            fixedPrice,
            sku,
            qty: qty || 1,
            subscription_id: isSubscriptionActive ? subscription_id || '' : '',
            discountType,
            subscriptionFrequency,
            additional_subscription_price: additional_subscription_price || 0,
            discount,
            savings: calculateSavings(discountType, discount, fixedPrice, savings, qty),
            subscriptionDiscount: custom_subscription_price ? 'allCycles' : 'firstBill',
            custom_subscription_price: custom_subscription_price || null
          };
        }),
      };

      handleSetValues(initialValues);
    } else {
      setIsLoading(false);
    }
  }, [selectedQuote?.selectedItems]);

  const handleSave = async (values) => {
    const selectedItems = values.items.map((item) => {
      const { sku, discountType, discount, qty, subscription_id, _id, fixedPrice, custom_subscription_price } = item;

      return {
        _id,
        sku,
        qty,
        price: fixedPrice,
        custom_subscription_price,
        product_option: {
          extension_attributes: {
            quotepro_discounts: {
              price_type: discountType,
              price: discount,
            },
            subscription_id
          },
        },
      };
    });

    const updatedDraftQuote = {
      ...selectedQuote,
      selectedItems,
    };

    const isDraftQuoteUpdated = await updateDraftQuoteByIdApi({
      dispatch,
      updatedDraftQuoteId: selectedQuote._id,
      updatedDraftQuote
    });

    if (isDraftQuoteUpdated && selectedItems.length > 0) {
      history.push(`/quoting/profile/${id}/quote-workspace/patient-activity`);
    }
  };

  const validationSchema = useMemo(() => createValidationSchema(
    quoteProductsByGroup,
    selectedQuote,
    subscriptionOptionsBySku,
    handleSelectDataSource,
    calculatedTotals
  ), [
    selectedQuote,
    quoteProductsByGroup,
    subscriptionOptionsBySku,
    calculatedTotals
  ]);

  const formik = useFormik({
    initialValues: {
      selectedGroup: '',
      items: [
        {
          selectedItemGroup: '',
          sku: '',
          fixedPrice: 0,
          qty: 1,
          subscription_id: '',
          discountType: '',
          subscriptionFrequency: '',
          additional_subscription_price: 0,
          savings: 0,
          discount: '',
          subscriptionDiscount: 'firstBill',
          custom_subscription_price: null
        },
      ],
    },
    onSubmit: handleSave,
    validationSchema
  });

  const {
    values,
    isValid,
    dirty,
    handleSubmit,
    setValues
  } = formik;

  useEffect(() => {
    setCalculatedTotals(calculateTotals(values.items, customerPushCart, quoteProducts));
  }, [values.items]);

  const handleAddMoreItem = () => {
    const { items } = values;
    const updatedItems = [...items];
    const lastItemGroup = updatedItems[updatedItems.length - 1]?.selectedItemGroup || '';

    const newItem = {
      selectedItemGroup: lastItemGroup,
      sku: '',
      fixedPrice: 0,
      qty: 1,
      subscription_id: '',
      subscriptionDiscount: 'firstBill',
      custom_subscription_price: null,
      discountType: '',
      subscriptionFrequency: '',
      additional_subscription_price: 0,
      savings: 0,
      discount: ''
    };

    updatedItems.push(newItem);

    const updatedValues = {
      ...values,
      items: updatedItems,
      selectedGroup: lastItemGroup,
    };

    setValues(updatedValues);
  };

  const handleSetValues = useCallback(async (initialValues) => {
    await setValues({ selectedGroup: '', items: initialValues.items });

    setTimeout(() => setIsLoading(false), 0);
  }, []);

  const handleCancel = () => {
    dispatch(setSelectedDraftedQuote({}));
    history.push(`/quoting/profile/${id}/quote-workspace/patient-activity`);
  };

  const isDisabledButtonAction = loading || !(isValid && dirty);

  return (
    <>
      <Grid container spacing={2}>
        <form autoComplete="off" className="w-full" onSubmit={handleSubmit}>
          {isLoading && (
            <Grid container spacing={2} justifyContent="center" className="p-4">
              <CircularProgress color="inherit" size={20} />
            </Grid>
          )}
          {!isLoading && (
            <>
              <div className={classes.quoteGroupDotted}>
                <EditQuoteListingsGroupSelect
                  formik={formik}
                />
                <div className="mt-2">
                  {values?.items?.length && values?.items?.map((item, index) => (
                    <Fragment key={`selectedItemId-${index}-${item._id}`}>
                      <EditQuoteListingsItemRow
                        item={item}
                        index={index}
                        selectedQuote={selectedQuote}
                        formik={formik}
                      />
                    </Fragment>
                  ))}
                </div>
              </div>
              <EditQuoteListingsFooter
                formik={formik}
                loading={loading}
                calculatedTotals={calculatedTotals}
                handleAddMoreItem={handleAddMoreItem}
              />
            </>
          )}
          <div className="flex flex-row align-center justify-between mt-4">
            <Button
              variant="outlined"
              color="primary"
              className="uppercase"
              onClick={() => handleCancel()}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={isDisabledButtonAction}
            >
              Save
            </Button>
          </div>
        </form>
      </Grid>
      <BackdropCircular loading={loading} />
    </>
  );
};

export default EditQuoteListings;
